import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.number.is-finite.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import config from 'utils/config';
import { hasPrivilege } from 'utils/privileges';
import { HELIX_AUDIENCE_CONFIG_KEYS } from 'types/helixDisplay';

/*
 * The most decimal places an averaged score can be displayed to. Averaged scores are
 * stored to 2dp, so anything beyond this would only ever render trailing zeros.
 * Mirrors MAX_SCORE_DECIMAL_PLACES in api/utils/helix.js.
 */
export var MAX_SCORE_DECIMAL_PLACES = 2;
/*
 * Mirrors isHelixAdminViewer in the API: anyone with the admin role (which owners also carry)
 * or a super user can be shown the admin display settings.
 */
export function isHelixAdminViewer() {
  return Boolean(hasPrivilege('admin'));
}

// One view's setting, read on its own with no fallback between views - mirrors the API
export function getAudienceSetting(helixConfig, isAdmin, field) {
  return helixConfig[HELIX_AUDIENCE_CONFIG_KEYS[isAdmin ? 'admin' : 'user'][field]];
}

// The settings page's colour picker only gives #rrggbb. Anything else is refused, as the colour
// goes straight into CSS, where another value could add rules of its own
var HEX_COLOUR = /^#[\da-f]{6}$/i;

// Mirrors getScoreBands in the API, so both work out the same band for a score
function getScoreBands(helixConfig) {
  var helixScoreBands = helixConfig.helixScoreBands;
  if (!Array.isArray(helixScoreBands)) {
    return [];
  }
  return helixScoreBands.filter(function (band) {
    return Number.isFinite(band === null || band === void 0 ? void 0 : band.min) && typeof band.label === 'string' && typeof band.colour === 'string' && HEX_COLOUR.test(band.colour);
  }).map(function (_ref) {
    var min = _ref.min,
      label = _ref.label,
      colour = _ref.colour;
    return {
      min: min,
      label: label,
      colour: colour
    };
  }).sort(function (a, b) {
    return a.min - b.min;
  });
}
/**
 * The Helix display settings for one view - the admin settings or the user settings. Mirrors
 * getHelixUIMode in the API, which applies the same settings to what it returns. Components get
 * the view they're shown in from `useHelixUIMode`; see `HelixAdminViewProvider`.
 * @param options - Options
 * @param options.isAdminView - Whether to use the admin display settings
 * @returns The view's display settings
 */
export default function getHelixUIMode() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$isAdminView = _ref2.isAdminView,
    isAdmin = _ref2$isAdminView === void 0 ? false : _ref2$isAdminView;
  var helixConfig = config || {};
  var helixUIMode = getAudienceSetting(helixConfig, isAdmin, 'uiMode');
  var showFeedback = helixUIMode !== 'onlyScores';
  var showScores = helixUIMode !== 'onlyFeedback';
  var showOverallScore = getAudienceSetting(helixConfig, isAdmin, 'showOverallScore') === true;
  var scoreBands = getScoreBands(helixConfig);
  var showScoreBands = scoreBands.length > 0 && getAudienceSetting(helixConfig, isAdmin, 'showScoreBands') === true;
  var showTranscript = getAudienceSetting(helixConfig, isAdmin, 'showTranscript') === true;
  var scoreOutOf = Number(helixConfig.helixScoreOutOf) || 100;
  // Stored by a select input, so the value arrives as a string - coerce and clamp it
  var scoreDecimalPlaces = Math.min(MAX_SCORE_DECIMAL_PLACES, Math.max(0, Number(helixConfig.helixScoreDecimalPlaces) || 0));
  return {
    helixUIMode: helixUIMode,
    showFeedback: showFeedback,
    showScores: showScores,
    showOverallScore: showOverallScore,
    showScoreBands: showScoreBands,
    showTranscript: showTranscript,
    scoresNormalisedToBands: !showScores && showScoreBands,
    scoreBands: showScoreBands ? scoreBands : [],
    scoreOutOf: scoreOutOf,
    scoreDecimalPlaces: scoreDecimalPlaces
  };
}